import { useEffect, useMemo } from "react";
import {
  Button,
  CardNotification,
  Checkbox,
  Heading,
  Loading,
  RadioButton,
  Select,
  Table,
  type TableColumn,
  Text,
  Tooltip,
  RoundButton,
} from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import { useTranslation } from "react-i18next";
import SelectSection from "@modules/dimensions/section/create-section/components/select-section";
import {
  useCurriculumTeachingDates,
  useExistingLeagueOptions,
  usePopulationCourses,
  type ExistingLeagueOption,
} from "@services/domains/packages";
import type {
  PopulationSubject,
  SectionConfiguration,
  SubjectConfiguration,
  ValidationPresentation,
} from "../../types";
import {
  selectCanOfferInitialGroups,
  selectHasIncompleteExistingReferences,
} from "../../store/packageWithLeaguesStore";
import usePackageWithLeaguesStore from "../../hooks/use-package-with-leagues-store";
import usePackageWithLeaguesStoreApi from "../../hooks/use-package-with-leagues-store-api";
import { getCurriculumTeachingDatesDisplay } from "../../utils/curriculum-teaching-dates";
import { resolveGroupVacancySource } from "../../utils/group-vacancy-source";
import { validateLeagueConfiguration } from "../../utils/wizard-validation";
import PackageConfigurationSummary from "../package-configuration-summary";
import ValidationMessageList from "../validation-message-list";
import css from "./leagueConfigurationStep.module.scss";

interface LeagueConfigurationStepProps {
  scenarioId: string;
  originId: string;
  onBack: () => void;
  onCreate: () => Promise<unknown>;
  isSubmitting: boolean;
  shouldContinueToGroups: boolean;
}

interface SubjectLeagueConfigurationProps {
  configuration: SubjectConfiguration;
  scenarioId: string;
  originId: string;
  excludedLeagueIds: string[];
}

const EMPTY_VALIDATIONS: ValidationPresentation[] = [];

const SubjectLeagueActions = ({ configuration }: { configuration: SubjectConfiguration }) => {
  const { t } = useTranslation();
  const store = usePackageWithLeaguesStoreApi();
  const { subject, choice } = configuration;

  return (
    <div className={css.subjectLeagueConfiguration_choices}>
      <RadioButton
        className={css.subjectLeagueConfiguration_componentChoice}
        labelRight={t("package-wizard.leagues.create-new")}
        checked={choice.kind === "create-new"}
        disabled={!subject.canCreateNewLeague}
        onChange={() => store.getState().selectCreateNewLeague(subject.courseId)}
      />
      <RadioButton
        className={css.subjectLeagueConfiguration_componentChoice}
        labelRight={t("package-wizard.leagues.use-existing")}
        checked={choice.kind === "use-existing"}
        onChange={() => store.getState().selectExistingLeague(subject.courseId)}
      />
      <RadioButton
        className={css.subjectLeagueConfiguration_componentChoice}
        labelRight={t("package-wizard.leagues.omit")}
        checked={choice.kind === "omit"}
        onChange={() => store.getState().omitSubject(subject.courseId)}
      />
    </div>
  );
};

const SubjectLeagueConfiguration = ({
  configuration,
  scenarioId,
  originId,
  excludedLeagueIds,
}: SubjectLeagueConfigurationProps) => {
  const { t } = useTranslation();
  const store = usePackageWithLeaguesStoreApi();
  const subjectErrors = usePackageWithLeaguesStore(
    state => state.validationMessagesByCourseId[configuration.subject.courseId],
  );
  const errors = subjectErrors ?? EMPTY_VALIDATIONS;
  const { subject, choice } = configuration;
  const existingLeagues = useExistingLeagueOptions({
    scenarioId,
    originId,
    courseId: subject.courseId,
    excludedLeagueIds,
    enabled: choice.kind === "use-existing",
  });

  const setSection = (courseComponentId: string, section: SectionConfiguration) => {
    if (choice.kind !== "create-new") return;
    const nextSections = choice.sections.map(value =>
      value.courseComponentId === courseComponentId ? section : value,
    );
    store.getState().setSectionConfigurations(subject.courseId, nextSections);
  };

  if (choice.kind === "omit" && errors.length === 0) return null;

  return (
    <div className={css.subjectLeagueConfiguration}>
      {choice.kind === "use-existing" && (
        <div className={css.subjectLeagueConfiguration_existingLeague}>
          {existingLeagues.error && (
            <CardNotification state="error" outlined title={t("package-wizard.common.error-title")}>
              <Button variant="ghost" onClick={() => existingLeagues.retry()}>
                {t("package-wizard.actions.retry")}
              </Button>
            </CardNotification>
          )}

          {!existingLeagues.error && existingLeagues.isEmpty && (
            <Text type="sm">{t("package-wizard.leagues.no-existing")}</Text>
          )}

          {!existingLeagues.error && !existingLeagues.isEmpty && (
            <Select
              label={t("package-wizard.leagues.existing-selector")}
              placeholder={t("package-wizard.leagues.existing-placeholder")}
              disabled={existingLeagues.isLoading}
              isLoading={existingLeagues.isLoading}
              options={existingLeagues.options}
              value={
                existingLeagues.options.find(option => option.value === choice.existingLeagueId) ??
                null
              }
              onChange={(option: ExistingLeagueOption | null) =>
                store
                  .getState()
                  .selectExistingLeague(
                    subject.courseId,
                    option?.value ?? null,
                    option?.groupCodes ?? [],
                  )
              }
            />
          )}
        </div>
      )}

      {choice.kind === "create-new" && (
        <div className={css.subjectLeagueConfiguration_sections}>
          {subject.components.map(component => {
            const selected = choice.sections.find(
              section => section.courseComponentId === component.courseComponentId,
            );

            return (
              <div
                className={css.subjectLeagueConfiguration_component}
                key={component.courseComponentId}
              >
                <Text className={css.subjectLeagueConfiguration_componentName} type="sm">
                  {component.name}
                </Text>

                <div className={css.subjectLeagueConfiguration_componentChoices}>
                  <RadioButton
                    className={css.subjectLeagueConfiguration_componentChoice}
                    labelRight={t("create-group-link.step-2.component.new-section.label")}
                    checked={selected?.kind === "create-new"}
                    onChange={() =>
                      setSection(component.courseComponentId, {
                        kind: "create-new",
                        courseComponentId: component.courseComponentId,
                      })
                    }
                  />

                  <RadioButton
                    className={css.subjectLeagueConfiguration_componentChoice}
                    labelRight={t("create-group-link.step-2.component.section-existing.label")}
                    checked={selected?.kind === "use-existing"}
                    onChange={() =>
                      setSection(component.courseComponentId, {
                        kind: "use-existing",
                        courseComponentId: component.courseComponentId,
                        sectionId: "",
                      })
                    }
                  />
                </div>

                {selected?.kind === "use-existing" && (
                  <div className={css.subjectLeagueConfiguration_sectionSelect}>
                    <SelectSection
                      courseId={Number(subject.courseId)}
                      courseComponentId={component.courseComponentId}
                      placeholder={t(
                        "create-group-link.step-2.component.select-search-section.placeholder",
                      )}
                      onChange={option =>
                        setSection(component.courseComponentId, {
                          kind: "use-existing",
                          courseComponentId: component.courseComponentId,
                          sectionId: option?.value ?? "",
                        })
                      }
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ValidationMessageList messages={errors} />
    </div>
  );
};

const LeagueConfigurationStep = ({
  scenarioId,
  originId,
  onBack,
  onCreate,
  isSubmitting,
  shouldContinueToGroups,
}: LeagueConfigurationStepProps) => {
  const { t } = useTranslation();
  const store = usePackageWithLeaguesStoreApi();
  const packageDetails = usePackageWithLeaguesStore(state => state.packageDetails);
  const subjects = usePackageWithLeaguesStore(state => state.subjects);
  const canCreateWithoutNewSections = usePackageWithLeaguesStore(
    state => state.settings.canCreateLinkWithoutNewSections,
  );
  const canOfferInitialGroups = usePackageWithLeaguesStore(selectCanOfferInitialGroups);
  const hasIncompleteExistingReferences = usePackageWithLeaguesStore(
    selectHasIncompleteExistingReferences,
  );
  const shouldCreateInitialGroups = usePackageWithLeaguesStore(
    state => state.shouldCreateInitialGroups,
  );
  const globalMessages = usePackageWithLeaguesStore(state => state.globalValidationMessages);
  const populationCourses = usePopulationCourses({
    scenarioId,
    originId,
    termId: packageDetails.termId,
    populationCode: packageDetails.population?.code ?? "",
  });
  const curriculumContext = useCurriculumTeachingDates({
    scenarioId,
    programId: packageDetails.population?.programId,
    curriculumCode: packageDetails.population?.curriculum ?? "",
  });

  const populationSubjects = useMemo<PopulationSubject[]>(
    () =>
      populationCourses.courses.map(course => ({
        courseId: course.id,
        code: course.code,
        name: course.name,
        components: (course.courseComponents ?? []).map(courseComponent => ({
          courseComponentId: courseComponent.id,
          code: courseComponent.component?.code ?? "",
          name: courseComponent.component?.name ?? courseComponent.component?.code ?? "",
        })),
        canCreateNewLeague: course.canEdit !== false && Boolean(course.courseComponents?.length),
        vacancySource: resolveGroupVacancySource(course?.tags),
      })),
    [populationCourses.courses],
  );

  useEffect(() => {
    if (!populationSubjects.length || subjects.length) return;
    store.getState().initializeSubjects(populationSubjects);
  }, [populationSubjects, store, subjects.length]);

  const excludedLeagueIds = useMemo(
    () =>
      subjects.flatMap(configuration =>
        configuration.choice.kind === "use-existing" && configuration.choice.existingLeagueId
          ? [configuration.choice.existingLeagueId]
          : [],
      ),
    [subjects],
  );
  const columns = useMemo<TableColumn[]>(
    () => [
      {
        key: "subject",
        header: t("package-wizard.groups.subject"),
        renderCell: data => {
          const { subject } = data as SubjectConfiguration;
          return (
            <Text>
              {subject.code} - {subject.name}
            </Text>
          );
        },
      },
      {
        key: "configuration",
        header: t("package-wizard.leagues.title"),
        renderCell: data => <SubjectLeagueActions configuration={data as SubjectConfiguration} />,
        desktopOptions: {
          headCellClassName: css.leagueConfigurationStep_configHead,
        },
      },
    ],
    [t],
  );

  const handleContinue = async () => {
    const errors = validateLeagueConfiguration(subjects, canCreateWithoutNewSections, t);
    store.getState().clearValidationMessages();
    errors.forEach((messages, courseId) =>
      store.getState().setSubjectValidationMessages(courseId, messages),
    );
    if (errors.size) return;
    if (shouldContinueToGroups) {
      const sourceKey = subjects
        .filter(configuration => configuration.choice.kind === "create-new")
        .map(configuration => configuration.subject.courseId)
        .sort()
        .join("|");
      store.getState().prepareInitialGroupConfiguration(sourceKey);
      store.getState().goForward();
      return;
    }
    await onCreate();
  };

  const handleGoBack = () => {
    store.getState().goBack();
  };

  const handleRetry = () => {
    if (populationCourses.error) void populationCourses.retry();
    if (curriculumContext.error) void curriculumContext.retry();
  };

  if (populationCourses.isLoading || curriculumContext.isLoading) return <Loading />;

  if (populationCourses.error || curriculumContext.error) {
    return (
      <CardNotification state="error" outlined title={t("package-wizard.common.error-title")}>
        <Button variant="ghost" onClick={handleRetry}>
          {t("package-wizard.actions.retry")}
        </Button>
      </CardNotification>
    );
  }

  if (populationCourses.isEmpty) {
    return (
      <CardNotification state="info" outlined title={t("package-wizard.leagues.empty-title")}>
        {t("package-wizard.leagues.empty-description")}
      </CardNotification>
    );
  }

  const teachingDates = getCurriculumTeachingDatesDisplay(curriculumContext.tags);

  return (
    <div className={css.leagueConfigurationStep}>
      <div className={css.leagueConfigurationStep_title}>
        <RoundButton size="sm" variant="ghost" icon="chevron-left" onClick={handleGoBack} />
        <Heading type="h2">{t("package-wizard.leagues.enabled-subjects")}</Heading>
        <Tooltip label={t("package-wizard.leagues.tooltip")} placement="top">
          <Icon name="circle-info" />
        </Tooltip>
      </div>

      <PackageConfigurationSummary packageDetails={packageDetails} teachingDates={teachingDates} />

      <Table
        className={css.leagueConfigurationStep_table}
        columns={columns}
        data={subjects}
        rowOptions={{
          renderCustomContent: data => {
            const configuration = data as SubjectConfiguration;

            return (
              <SubjectLeagueConfiguration
                configuration={configuration}
                scenarioId={scenarioId}
                originId={originId}
                excludedLeagueIds={excludedLeagueIds.filter(
                  id =>
                    configuration.choice.kind !== "use-existing" ||
                    id !== configuration.choice.existingLeagueId,
                )}
              />
            );
          },
        }}
        desktopVariant="regular"
      />

      {canOfferInitialGroups && (
        <Checkbox
          labelRight={t("package-wizard.leagues.create-initial-groups")}
          checked={shouldCreateInitialGroups}
          onChange={event => store.getState().setShouldCreateInitialGroups(event.target.checked)}
        />
      )}

      <ValidationMessageList variant="notification" messages={globalMessages} />

      <div className={css.leagueConfigurationStep_actions}>
        <Button variant="ghost" disabled={isSubmitting} onClick={onBack}>
          {t("package-wizard.actions.back")}
        </Button>
        <Button disabled={isSubmitting || hasIncompleteExistingReferences} onClick={handleContinue}>
          {shouldContinueToGroups
            ? t("package-wizard.actions.continue")
            : t("package-wizard.actions.create")}
        </Button>
      </div>
    </div>
  );
};

export default LeagueConfigurationStep;
